import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import * as React from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { SuccessMessageControl } from "@tlc/ls2pac/app/controls/successMessageControl";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { ListService, ListType } from "../ListService";
import { FormTextField } from "@tlc/ls2pac/components/FormTextField";

// Dialog Requires Module Augmentation see this post
// https://github.com/mui/material-ui/issues/45142
declare module "@mui/material/Dialog" {
  interface DialogPaperSlotPropsOverrides {
    noValidate: boolean;
    autoComplete: string;
    onSubmit?: React.FormEventHandler<HTMLFormElement>;
  }
}
export interface RenameListDialogProps {
  open: boolean;
  name: string;
  id: number;
  type: ListType;
  title: string;
  errorMessage: string;
  successMessage: string;
  onClose: () => void;
  onSave: (newName: string) => void;
}

export interface RenameListFormInput {
  listName: string;
}

export const RenameListDialog: React.FC<RenameListDialogProps> = ({
  name,
  id,
  type,
  open,
  title,
  errorMessage,
  successMessage,
  onClose,
  onSave,
}) => {
  const { translate } = useLanguage();
  const [renameError, setRenameError] = React.useState<boolean>(false);

  const formMethods = useForm<RenameListFormInput>({
    mode: "onBlur",
    defaultValues: {
      listName: name || "",
    },
  });
  const { handleSubmit, reset } = formMethods;
  

  const onSubmit: SubmitHandler<RenameListFormInput> = async ({ listName }) => {
    setRenameError(false);

    try {
      const success = await ListService.renameList(id, type, listName);

      if (success) {
        SuccessMessageControl.show(successMessage);
        onSave(listName);
        reset();
      } else {
        setRenameError(true);
      }
    } catch (e) {
      setRenameError(true);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      slotProps={{
        paper: {
          component: "form",
          noValidate: true,
          autoComplete: "off",
          onSubmit: handleSubmit(onSubmit),
        }
      }}
    >
      <FormProvider {...formMethods}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent sx={{ pb: 0 }}>
          {renameError && <Alert severity="error">{errorMessage}</Alert>}
          <FormTextField<RenameListFormInput>
            name="listName"
            fullWidth
            style={{ marginTop: "16px" }}
            label={title}
            required
            autoFocus
            maxLength={255}
          ></FormTextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} type="button">
            {translate("cancel")}
          </Button>
          <Button type="submit">{translate("ok")}</Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};
